/**
 * Front Desk - React Query hooks. Share/Portfolio pattern: one key namespace
 * and a whole-module invalidation on mutation settle (lists are small;
 * precision invalidation is not worth the drift).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './desk';

export const deskKeys = {
  all: ['desk'] as const,
  me: () => [...deskKeys.all, 'me'] as const,
  catalog: () => [...deskKeys.all, 'catalog'] as const,
  team: (id: number) => [...deskKeys.all, 'team', id] as const,
  requestType: (id: number) => [...deskKeys.all, 'request-type', id] as const,
  tickets: (opts: Parameters<typeof api.fetchTickets>[0]) => [...deskKeys.all, 'tickets', opts] as const,
  ticket: (n: number) => [...deskKeys.all, 'ticket', n] as const,
};

function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: deskKeys.all });
}

export const useDeskMe = () => useQuery({ queryKey: deskKeys.me(), queryFn: api.fetchMe, staleTime: 5 * 60_000 });

export const useCatalog = () => useQuery({ queryKey: deskKeys.catalog(), queryFn: api.fetchCatalog, staleTime: 5 * 60_000 });

export const useTeam = (id: number | undefined) =>
  useQuery({ queryKey: deskKeys.team(id ?? 0), queryFn: () => api.fetchTeam(id!), enabled: id != null, staleTime: 60_000 });

export const useRequestType = (id: number | undefined) =>
  useQuery({ queryKey: deskKeys.requestType(id ?? 0), queryFn: () => api.fetchRequestType(id!), enabled: id != null });

export const useTickets = (opts: Parameters<typeof api.fetchTickets>[0], enabled = true) =>
  useQuery({ queryKey: deskKeys.tickets(opts), queryFn: () => api.fetchTickets(opts), enabled });

export const useTicket = (n: number | undefined) =>
  useQuery({ queryKey: deskKeys.ticket(n ?? 0), queryFn: () => api.fetchTicket(n!), enabled: n != null, retry: false });

export function useCreateTicket() {
  const invalidate = useInvalidateAll();
  return useMutation({ mutationFn: api.createTicket, onSettled: invalidate });
}

/** Ticket edits take the number per call, so the board can move any card. */
export function useUpdateTicket() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ number, patch }: { number: number; patch: api.TicketPatch }) => api.updateTicket(number, patch),
    onSettled: invalidate,
  });
}

export function useAddComment(n: number) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: { kind: 'public' | 'internal'; body: string }) => api.addComment(n, input),
    onSettled: invalidate,
  });
}

export function useAnswerResolution(n: number) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: { solved: boolean; comment?: string }) => api.answerResolution(n, input),
    onSettled: invalidate,
  });
}

export function useSetParticipant(n: number) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: { person?: api.PersonRef; present: boolean }) => api.setParticipant(n, input),
    onSettled: invalidate,
  });
}

export function useSetLink(n: number) {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: { kind: api.LinkKind; otherNumber: number; present: boolean }) => api.setLink(n, input),
    onSettled: invalidate,
  });
}
